// tslint:disable:max-classes-per-file

import { Action, Reducer } from "redux";
import _ from "underscore";

import * as ListUtil from "../helpers/arrayHelpers";
import { LocalStorageHelper } from "@travellocal/utils/localStorageHelper";
import { neverHere } from "@travellocal/utils/tsHelpers";

const isUserState = (x): x is IState<IUser> => (x as IState<IUser>).status != null;
const isUser = (x): x is IUser => (x as IUser).id != null && (x as IUser).userType != null;

export const Auth = {
  getUserState: (): IState<IUser>|null => {
    let newState: IState<IUser> = null;

    let userOrState: IUser|IState<IUser>;
    try {
      userOrState = JSON.parse(LocalStorageHelper.getItem("user"));
    }
    catch (e) {
      // We don't care
    }

    if (userOrState) {
      let authContext: any;
      try {
        authContext = JSON.parse(LocalStorageHelper.getItem("authcontext"));
      }
      catch (e) {
        // We don't care
      }

      if (!authContext) {
        authContext = {};
      }

      if (isUserState(userOrState)) {
        authContext.migrated = true;

        const auth = {
          token: LocalStorageHelper.getItem("authtoken"),
          expires: LocalStorageHelper.getItem("authtokenExpires"),
          context: authContext,
        };

        Auth.saveState(userOrState.data, auth); // migrate old login format
        newState = new State<IUser>(userOrState.data, "OK", authContext);
      }
      else if (isUser(userOrState)) {
        newState = new State<IUser>(userOrState, "OK", authContext);
      }
    }

    return newState;
  },

  saveState: (user: IUser, auth?: { token: string, expires?: string, context?: object }) => {
    LocalStorageHelper.setItem("user", JSON.stringify(user));

    // Allow updating user profile without clearing auth state
    if (auth) {
      LocalStorageHelper.setItem("authtoken", auth.token);
      LocalStorageHelper.setItem("authtokenExpires", auth.expires || null); // TODO this is not set if logged in from the enquiry form
      LocalStorageHelper.setItem("authcontext", auth.context ? JSON.stringify(auth.context) : null);
    }
  },

  clearState: () => {
    LocalStorageHelper.removeItem("authtokenExpires");
    LocalStorageHelper.removeItem("authtoken");
    LocalStorageHelper.removeItem("authcontext");
    LocalStorageHelper.removeItem("user");
  },
};
export const SnippetsState = {
  clearState: (x) => {
    LocalStorageHelper.removeItem(x);
  },
};

export class State<T> implements IState<T>{
  public constructor(
    public data: T = null,
    public status: StateStatus = "OK",
    public context?: any,
    public error?: Error,
  ) {
  }
}

export class KeyedState<T> implements IKeyedState<T> {
  public constructor(
    public data: { [ key: string ]: T } = {},
    public status: StateStatus = "OK",
    public context?: any,
    public error?: Error,
  ) {
  }
}

export class PagerState<T> implements IPagerState<T> {
  public data: T[];
  public status: StateStatus;
  public context: { page: number } & object;
  public nextIndex: number;

  constructor(data: T[] = [], status: StateStatus = "OK", context: any = null, nextIndex: number = null) {
    this.data = data;
    this.status = status;
    this.context = context;
    this.nextIndex = nextIndex;
  }
}

export function isMatch(type: string, compare: string|string[]) {
  const matchSingle = (x: string) => type.split("_", 1)[0] === x;

  return compare instanceof Array
    ? compare.some(x => matchSingle(x))
    : type === compare || matchSingle(compare);
}

/**
 * redux-promise-middleware appends the status to the action type.
 */
export function getPayloadActionStatus<T>(action: IPayloadAction<T>): "PENDING"|"OK" {
  if (isPayloadAction(action)) {
    if (action.type.indexOf("_PENDING") > -1) {
      return "PENDING";
    }
    else {
      return "OK";
    }
  }
  else {
    neverHere(action);
  }
}

export function isPayloadAction<T>(action: IStoreAction<T>): action is IPayloadAction<T> {
  return action.type.indexOf("_REJECTED") < 0;
}

export function isErrorAction<T>(action: IStoreAction<T>): action is IErrorAction {
  return action.error || action.type.indexOf("_REJECTED") > -1;
}

export function stateFactory<TP, TR>(
  parseType: string|string[],
  payloadTransform: (source: TR, payload: TP) => TR,
  onMatch?: (newState: State<TR>, p: TP|Error) => void,
): Reducer<State<TR>> {
  return (state: State<TR>, action: IStoreAction<TP>) => {
    if (!state) {
      state = new State(null, "OK");
    }

    if (!isMatch(action.type, parseType)) {
      return state;
    }

    let newState: State<TR>;
    if (isPayloadAction(action)) {
      const status = getPayloadActionStatus(action);

      switch (status) {
        case "OK":
          const payload = payloadTransform(state.data, action.payload);
          newState = new State(payload, "OK", action.meta);
          break;
        case "PENDING":
          newState = new State(state.data, "PENDING", state.context);
          break;
        default:
          neverHere(status);
      }
    }
    else if (isErrorAction(action)) {
      // Persist data when receiving an error.
      newState = new State(state.data, "ERROR", state.context, action.payload);
    }
    else {
      neverHere(action);
    }

    if (onMatch) {
      onMatch(newState, action.payload);
    }

    return newState || state;
  };
}

/**
 * Parse the payload of an action into the state tree
 */
export function fromPayload<TP, TR>(types: string|string[], payloadTransform: (p: TP) => TR, onMatch?: (newState: State<TR>, payload: TP|Error) => void): Reducer<State<TR>> {
  return stateFactory<TP, TR>(types, (s, p) => payloadTransform(p), onMatch);
}

/**
 * Specialised form of fromPayload to parse result of fetcher class
 */
export function fromApi<TP, TR>(types: string|string[], payloadTransform: (payload: TP) => TR, onMatch?: (newState: State<TR>) => void): Reducer<State<TR>> {
  return stateFactory<IFetchApiResponse<TP>, TR>(types, (s, p) => p ? payloadTransform(p.json) : null, onMatch);
}

/**
 * Preserve external state unless action status is Ok, in which case the state of the inner reducer is appended.
 */
export function appendPayload<TP, TR>(type: string|string[], payloadTransform: (payload: TP) => TR, onMatch?: (newState: State<TR[]>) => void): Reducer<State<TR[]>> {
  const append = (source: TR[], payload: TP) => (source || []).concat(payloadTransform(payload));
  return stateFactory<TP, TR[]>(type, append, onMatch);
}

/**
 * Return null state.
 */
export function clear<TR>(onClear?: (oldState: State<TR>) => void): Reducer<State<TR>> {
  return (state: State<TR>, action: IStoreAction<any>) => {
    if (onClear) {
      onClear(state);
    }

    return new State<TR>(null, "OK", action.meta);
  };
}

/**
 * Apply several reducers, one after the other. If multiple reducers match, their effects will combine together.
 */
export function chain<TState>(...reducers: Reducer<TState>[]): Reducer<TState> {
  return (state: TState, action: IStoreAction<any>) => {
    let newState = state;
    for (const r of reducers) {
      newState = r(newState, action);
    }
    return newState;
  };
}

/**
 * Generate a reducer that will combine a single item with a list of items.
 * @param matchType Action types to respond to.
 * @param payloadTransform Function to transform the payload object into the shape we want in the redux store.
 * @param getId Function to get the id of an item. Used to detect similar items.
 * @param upsert Whether to insert new items if they are not present in the list already.
 * @param prepend Whether to place updated items at the head rather than the tail of the list. Defaults to false.
 */
function mergeSingle<TPayload, TStore>(
  matchType: string|string[],
  payloadTransform: (payload: TPayload) => TStore,
  getId: (item: TStore) => number,
  upsert: boolean,
  prepend: boolean = false,
): Reducer<PagerState<TStore>> {

  return (state: PagerState<TStore>, action: IStoreAction<any>) => {
    if (!isMatch(action.type, matchType) || !state) {
      return state || new PagerState<TStore>();
    }

    if (isPayloadAction(action)) {
      const status = getPayloadActionStatus(action);

      switch (status) {
        case "OK":
          const item = payloadTransform(action.payload);
          const itemId = getId(item);

          const shouldDoMerge = upsert || _.findIndex(state.data, i => getId(i) === itemId) > -1;
          let merged: TStore[];
          if (shouldDoMerge) {
            // If it's new, stick the item at either the start or the end of the list
            merged = ListUtil.mergeById(getId, state.data, [ item ], prepend);
          }
          else {
            // New array object to match redux semantics
            merged = [ ...state.data ];
          }

          return new PagerState<TStore>(merged, status, action.meta, state.nextIndex);
        case "PENDING":
          return new PagerState<TStore>(state.data, "PENDING", state.context, state.nextIndex);
        default:
          neverHere(status);
      }
    }
    else if (isErrorAction(action)) {
      return new PagerState<TStore>(state.data, "ERROR", state.context, state.nextIndex);
    }
    else {
      neverHere(action);
    }
  };
}

/**
 * Generate a reducer that upserts a single item payload into a PagerState.
 * An existing item with the same ID will be replaced.
 * @param matchType Action types the reducer should respond to.
 * @param payloadTransform Function to transform the payload object into the shape we store in Redux.
 * @param getId Function to get the id of an item. Used for comparison when merging lists.
 * @param prepend Whether the new item should .
 */
export function upsertSingle<TP, TR>(
  matchType: string|string[],
  payloadTransform: (payload: TP) => TR,
  getId: (item: TR) => number,
  prepend: boolean = false,
): Reducer<PagerState<TR>> {

  return mergeSingle(matchType, payloadTransform, getId, true, prepend);
}

/**
 * Generate a reducer that updates a PagerState with a single item payload.
 * If the item doesn't exist in the list already, it will be ignored.
 * @param matchType Action types te reducer should respond to.
 * @param payloadTransform Function to transform the payload object into the shape we store in Redux.
 * @param getId Function to get the id of an item. Used for comparison when merging lists.
 */
export function updateSingle<TP, TR>(
  matchType: string|string[],
  payloadTransform: (payload: TP) => TR,
  getId: (item: TR) => number,
): Reducer<PagerState<TR>> {

  return mergeSingle(matchType, payloadTransform, getId, false);
}

/**
 * Reducer for paging data. Handles deduplication of items in new pages, and maintains paging state using the nextIndex property.
 */
export function mergePage<TPayload, TStore>(
  matchType: string,
  payloadTransform: (payloadItem: TPayload) => TStore,
  getId: (item: TStore) => number,
): Reducer<PagerState<TStore>> {

  return (state: PagerState<TStore>, action: IStoreAction<IFetchApiResponse<IApiResponse<TPayload[]>>>) => {
    if (!state) {
      state = new PagerState<TStore>();
    }

    if (!isMatch(action.type, matchType)) {
      return state;
    }

    if (isPayloadAction(action)) {
      const status = getPayloadActionStatus(action);

      switch (status) {
        case "OK":
          const { payload, meta } = action;
          if (payload) {
            // If the paged api was called with page < 0, clear existing items from the state.
            const shouldReset = meta ? (meta.page as number || 1) < 0 : false;
            const oldItems = state.data || [];
            const newItems = payload.json.data.map(payloadTransform);
            const merged: TStore[] = shouldReset ? newItems : ListUtil.mergeById(getId, oldItems, newItems);
            const nextPage = payload.json.meta ? payload.json.meta.next : null;

            return new PagerState(merged, "OK", meta, nextPage);
          }
          else {
            return new PagerState(state.data, "OK", action.meta, state.nextIndex);
          }
        case "PENDING":
          return new PagerState(state.data, "PENDING", state.context, state.nextIndex);
        default:
          neverHere(status);
      }
    }
    else if (isErrorAction(action)) {
      return new PagerState(state.data, "ERROR", state.context, state.nextIndex);
    }
    else {
      neverHere(action);
    }
  };
}

/**
 * Pager reducer for old api format - component lists, review list
 */
export function pager<TStore>(
  state: PagerState<TStore> = new PagerState<TStore>(),
  action: IStoreAction<IFetchApiResponse<IPage<TStore>>>,
  getActionName: string,
): IPagerState<TStore> {

  if (!isMatch(action.type, getActionName)) {
    return state;
  }

  if (isPayloadAction(action)) {
    const status = getPayloadActionStatus(action);

    switch (status) {
      case "OK":
        // "reset" the state if the paged api was called with page number < 1.
        const shouldReset = action.meta ? (action.meta.page as number || 2) < 1 : false;
        const page = action.payload.json || {payload: []};
        const newItems = shouldReset ? page.payload : (state.data || []).concat(page.payload);

        return new PagerState(newItems, status, action.meta, page.next);
      case "PENDING":
        return new PagerState(state.data, "PENDING", action.meta, undefined);
      default:
        neverHere(status);
    }
  }
  else if (isErrorAction(action)) {
    return new PagerState(state.data, "ERROR", action.meta, undefined);
  }
  else {
    neverHere(action);
  }
}

/**
 * Apply a reducer on a subsection of the state tree.
 */
export function scope<T1, T2>(select: (s: T1) => T2, reducer: Reducer<T2>): (state: T1, action) => T2 {
  return (state: T1, action) => {
    return reducer(select(state), action);
  };
}

/**
 * Apply the reducer when the action type matches.
 */
export function when<T>(types: string|string[], reducer: Reducer<State<T>>): Reducer<State<T>> {
  return (state: State<T>, action: Action) => {
    return isMatch(action.type, types)
      ? reducer(state, action)
      : state;
  };
}

/**
 * Apply reducers to data sharded by a key. Use with IKeyedState.
 * @param matchType The actions this reducer should respond to. Should be a superset of inner reducer matchTypes.
 * @param valueReducer The reducer to apply to value found by providing a key.
 */
export const keyed = <TVal>(matchType: string|string[], valueReducer: Reducer<TVal>): Reducer<IKeyedState<TVal>> => {
  return (state: IKeyedState<TVal>, action: IStoreAction<any>) => {
    state = state || new KeyedState<TVal>();

    if (!isMatch(action.type, matchType)) {
      return state;
    }

    const actionKey = action.meta ? action.meta.key as string : null;
    if (!actionKey) {
      // TODO enforce that this prop exists through typed actions
      throw new Error(`Action ${action.type} must be dispatched with a context shaped { key: string } because it's for a keyedState`);
    }

    // Get the new state for the keyed value
    const scopedReducer = scope<IKeyedState<TVal>, TVal>(ks => ks && ks.data ? ks.data[actionKey] : null, valueReducer);
    const updatedValue = scopedReducer(state, action);

    // Set the updated value in a copy of the existing state
    const newData = { ...state.data };
    newData[actionKey] = updatedValue;

    // For debugging purposes - record which key we've just changed in the root state
    const context = {
      updatedKey: actionKey,
      updatedTimestamp: new Date().toUTCString(),
    };

    // TODO - update status of keyed container when inner things are pending. Might be useful
    return new KeyedState(newData, "OK", context);
  };
};
